<script setup>
// Imports
import { DateTime } from 'luxon'
import { useUserStore } from 'stores/userStore'
import { computed, nextTick, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'

// Props
const props = defineProps({
  mode: {
    type: String,
    default: 'date', // 'date' | 'time' | 'date-time'
  },
  modelValue: {
    type: null,
    required: true,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  emitInvalidInput: {
    type: Boolean,
    default: false,
  },
  emitNullOnEmptyInput: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Function,
    default: null,
  },
  noTodayBtn: {
    type: Boolean,
    default: false,
  },
  noNowBtn: {
    type: Boolean,
    default: false,
  },
  min: {
    type: String,
    default: '',
  },
  max: {
    type: String,
    default: '',
  },
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  respectTimeZoneOffset: {
    type: Boolean,
    default: false,
  },
  showWeekNumbers: {
    type: Boolean,
    default: true,
  },
  navigationMode: {
    type: Boolean,
    default: false,
  },
  navigationView: {
    type: String,
    default: 'month',
  },
  showDay: {
    type: Boolean,
    default: true,
  },
  showMonth: {
    type: Boolean,
    default: true,
  },
  showYear: {
    type: Boolean,
    default: true,
  },
  closeDateOnSelect: {
    type: Boolean,
    default: true,
  },
  closeTimeOnSelect: {
    type: Boolean,
    default: false,
  },
  useCustomTimePicker: {
    type: Boolean,
    default: true,
  },
  showClockSwitcher: {
    type: Boolean,
    default: true,
  },
})

// Emits
const emit = defineEmits(['update:modelValue'])

// Constants
const { t } = useI18n({ useScope: 'global' })
const userStore = useUserStore()

// Refs
const datePickerErrorMessage = ref(null)
const dateInputField = ref(null)
const selectedDate = ref(null)
const selectedTime = ref(null)
const navYearMonth = ref(null)
const navPopup = ref(null)
const datePopup = ref(null)
const timePopup = ref(null)
const timeAtPopupOpen = ref(null)
const customHours = ref('')
const customMinutes = ref('')
const customHoursInputRef = ref(null)
const customMinutesInputRef = ref(null)
const useCustomTimePickerLocal = ref(props.useCustomTimePicker)
const suppressCustomTimeWatch = ref(false)

// Computed
const navigationBase = computed(() => {
  const src = selectedDate.value || props.modelValue
  if (!src) {
    return null
  }
  const dt = DateTime.fromISO(String(src))
  return dt.isValid ? dt : null
})

const navYearMonthSource = computed(() => {
  if (navYearMonth.value) {
    return navYearMonth.value
  }

  const base = navigationBase.value || DateTime.local()
  return `${base.year}-${String(base.month).padStart(2, '0')}`
})

const navWeekNumbers = computed(() => {
  const ym = navYearMonthSource.value
  if (!ym) {
    return []
  }

  const [yearStr, monthStr] = ym.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)

  if (!year || !month) {
    return []
  }

  const monthStart = DateTime.local(year, month, 1)
  const firstCell = monthStart.startOf('week')
  const lastCell = monthStart.endOf('month').endOf('week')

  const result = []
  let cursor = firstCell

  while (cursor <= lastCell) {
    result.push({
      key: `${cursor.year}-${cursor.weekNumber}`,
      number: cursor.weekNumber,
    })
    cursor = cursor.plus({ weeks: 1 })
  }

  return result
})

const navSelectedDates = computed({
  get() {
    const base = navigationBase.value
    if (!base) {
      return []
    }

    if (props.navigationView === 'day') {
      return [base.toFormat('yyyy-LL-dd')]
    }

    if (props.navigationView === 'week') {
      const weekStart = base.startOf('week')
      const days = []
      for (let i = 0; i < 7; i += 1) {
        days.push(weekStart.plus({ days: i }).toFormat('yyyy-LL-dd'))
      }
      return days
    }

    const monthStart = base.startOf('month')
    const daysInMonth = monthStart.daysInMonth
    const days = []
    for (let i = 0; i < daysInMonth; i += 1) {
      days.push(monthStart.plus({ days: i }).toFormat('yyyy-LL-dd'))
    }
    return days
  },
  set(val) {
    const arr = Array.isArray(val) ? val : []
    const raw = arr.length ? arr[arr.length - 1] : null
    if (!raw) {
      return
    }
    const dt = DateTime.fromISO(String(raw))
    if (!dt.isValid) {
      return
    }
    const iso = dt.toISODate()
    selectedDate.value = iso
    emit('update:modelValue', iso)

    if (navPopup.value && props.closeDateOnSelect) {
      navPopup.value.hide()
    }
  },
})

const selectedDateProxy = computed({
  get() {
    return selectedDate.value
  },
  set(val) {
    const next = val || null
    const prev = selectedDate.value || null

    if (next === prev) {
      return
    }

    selectedDate.value = next

    updateModelValue()

    if (datePopup.value && props.closeDateOnSelect && !props.navigationMode) {
      datePopup.value.hide()
    }
  },
})

const displayValue = computed(() => {
  if (!props.modelValue) {
    return ''
  }

  if (props.navigationMode) {
    const dt = DateTime.fromISO(String(props.modelValue))
    if (!dt.isValid) {
      return ''
    }

    const lang = userStore.currentRegionalSettings.lang || 'en'
    let parts = []

    if (props.showDay) {
      parts.push('d')
    }
    if (props.showMonth) {
      parts.push('LLLL')
    }
    if (props.showYear) {
      parts.push('yyyy')
    }

    if (parts.length === 0) {
      parts = ['LLLL', 'yyyy']
    }

    let formatString = parts.join(' ')
    let text = dt.setLocale(lang).toFormat(formatString)

    if (!props.showDay && props.showMonth) {
      text = text.charAt(0).toUpperCase() + text.slice(1)
    }

    return text
  }

  if (props.mode === 'time') {
    return props.modelValue
  }

  const dt = props.respectTimeZoneOffset ? DateTime.fromISO(props.modelValue, { setZone: true }) : DateTime.fromISO(props.modelValue)

  if (!dt.isValid) {
    return props.emitInvalidInput ? String(props.modelValue) : ''
  }

  if (props.mode === 'date-time') {
    return dt.setLocale(userStore.currentRegionalSettings.lang).toFormat(userStore.currentRegionalSettings.dateTimeFormat || 'dd/MM/yyyy HH:mm')
  }

  return dt.setLocale(userStore.currentRegionalSettings.lang).toFormat(userStore.currentRegionalSettings.dateFormat || 'dd/MM/yyyy')
})

const currentMask = computed(() => {
  if (props.mode === 'time') {
    return userStore.currentRegionalSettings.timeMask || '##:##'
  }

  return props.mode === 'date-time' ? userStore.currentRegionalSettings.dateTimeMask : userStore.currentRegionalSettings.mask
})

// Methods
const toggleTimePickerMode = async () => {
  useCustomTimePickerLocal.value = !useCustomTimePickerLocal.value
  if (useCustomTimePickerLocal.value) {
    initCustomTimeFromSelected()
  }

  if (!timePopup.value) {
    return
  }

  await nextTick()

  timePopup.value.updatePosition()
}

const onNavNavigation = (payload) => {
  if (payload && payload.year && payload.month) {
    navYearMonth.value = `${payload.year}-${String(payload.month).padStart(2, '0')}`
  }
}

const onTimeChanged = (value) => {
  updateModelValue()

  if (!props.closeTimeOnSelect || useCustomTimePickerLocal.value || !timePopup.value) {
    return
  }

  const effective = typeof value === 'string' ? value : selectedTime.value
  const parts = String(effective || '').split(':')
  if (parts.length < 2) {
    return
  }

  const newMinutes = Number(parts[1])
  if (Number.isNaN(newMinutes)) {
    return
  }

  if (!timeAtPopupOpen.value) {
    timePopup.value.hide()
    return
  }

  const initialParts = timeAtPopupOpen.value.split(':')
  const initialMinutes = Number(initialParts[1])

  if (!Number.isNaN(initialMinutes) && initialMinutes !== newMinutes) {
    timePopup.value.hide()
  }
}

const updateModelValue = () => {
  if (props.mode === 'time') {
    if (!selectedTime.value) {
      emit('update:modelValue', null)
    } else {
      emit('update:modelValue', selectedTime.value)
    }
    return
  }

  if (!selectedDate.value) {
    emit('update:modelValue', null)
    return
  }

  let dt = DateTime.fromISO(selectedDate.value)
  if (props.mode === 'date-time' && selectedTime.value) {
    const [hour, minute] = selectedTime.value.split(':').map(Number)
    dt = dt.set({ hour, minute })
    emit('update:modelValue', dt.toISO())
  } else {
    emit('update:modelValue', dt.toISODate())
  }
}

const isEmptyClearableMaskedInput = (val) => {
  return (props.clearable || props.emitNullOnEmptyInput) && !/\d/.test(String(val || ''))
}

const onManualInput = (val) => {
  if (!val || isEmptyClearableMaskedInput(val)) {
    emit('update:modelValue', null)
    return
  }

  if (props.mode === 'time') {
    const timeFormat = userStore.currentRegionalSettings.timeFormat || 'HH:mm'
    const dt = DateTime.fromFormat(val, timeFormat)
    if (dt.isValid) {
      emit('update:modelValue', dt.toFormat(timeFormat))
    } else if (props.emitInvalidInput) {
      emit('update:modelValue', val)
    }
    return
  }

  const dtFormat = props.mode === 'date-time' ? userStore.currentRegionalSettings.dateTimeFormat : userStore.currentRegionalSettings.dateFormat

  const dt = DateTime.fromFormat(val, dtFormat)

  if (dt.isValid) {
    if (props.mode === 'date-time') {
      emit('update:modelValue', dt.toISO())
    } else {
      emit('update:modelValue', dt.toISODate())
    }
  } else if (props.emitInvalidInput) {
    // Editable tables retain incomplete input so their save validation cannot silently use the previous date.
    emit('update:modelValue', val)
  }
}

const validateOptions = (dateString) => {
  if (props.mode === 'time') {
    return true
  }

  const date = DateTime.fromISO(dateString.replaceAll('/', '-')).startOf('minute')
  if (props.min && date < DateTime.fromISO(props.min).startOf('day')) {
    return false
  }
  if (props.max && date > DateTime.fromISO(props.max).startOf('day')) {
    return false
  }
  if (props.options && typeof props.options === 'function') {
    return props.options(dateString)
  }
  return true
}

const parseTimeToMinutes = (input, timeFormat) => {
  if (!input) {
    return null
  }

  const raw = String(input).trim()
  if (!raw) {
    return null
  }

  let parsed = DateTime.fromFormat(raw, timeFormat)
  if (!parsed.isValid) {
    parsed = DateTime.fromISO(raw, { setZone: true })
  }

  if (!parsed.isValid) {
    const fallback = DateTime.fromFormat(raw, 'HH:mm')
    if (!fallback.isValid) {
      return null
    }
    parsed = fallback
  }

  return parsed.hour * 60 + parsed.minute
}

const validateDate = (value) => {
  datePickerErrorMessage.value = null

  if (!value || value.includes('_')) {
    return true
  }

  if (props.mode === 'time') {
    const timeFormat = userStore.currentRegionalSettings.timeFormat || 'HH:mm'
    const dt = DateTime.fromFormat(value, timeFormat)
    if (!dt.isValid) {
      datePickerErrorMessage.value = t('Shared.Globals.Invalid time', {
        format: timeFormat.toLowerCase(),
      })
      return datePickerErrorMessage.value
    }

    const valueMinutes = dt.hour * 60 + dt.minute

    if (props.min) {
      const minMinutes = parseTimeToMinutes(props.min, timeFormat)
      if (minMinutes !== null && valueMinutes < minMinutes) {
        const minDt = DateTime.fromObject({ hour: Math.floor(minMinutes / 60), minute: minMinutes % 60 })
        datePickerErrorMessage.value = t('Shared.Globals.Min start time', {
          minStartDate: minDt.setLocale(userStore.currentRegionalSettings.lang).toFormat(timeFormat),
        })
        return datePickerErrorMessage.value
      }
    }

    if (props.max) {
      const maxMinutes = parseTimeToMinutes(props.max, timeFormat)
      if (maxMinutes !== null && valueMinutes > maxMinutes) {
        const maxDt = DateTime.fromObject({ hour: Math.floor(maxMinutes / 60), minute: maxMinutes % 60 })
        datePickerErrorMessage.value = t('Shared.Globals.Max end time', {
          maxEndDate: maxDt.setLocale(userStore.currentRegionalSettings.lang).toFormat(timeFormat),
        })
        return datePickerErrorMessage.value
      }
    }

    return true
  }

  const dtFormat = props.mode === 'date-time' ? userStore.currentRegionalSettings.dateTimeFormat : userStore.currentRegionalSettings.dateFormat

  const dt = DateTime.fromFormat(value, dtFormat)

  if (!dt.isValid) {
    datePickerErrorMessage.value = t('Shared.Globals.Invalid date', {
      format: userStore.currentRegionalSettings.dateFormat?.toLowerCase() || 'dd/mm/yyyy',
    })
    return datePickerErrorMessage.value
  }

  if (props.min) {
    const minDateTime = DateTime.fromISO(props.min)
    if (dt < minDateTime) {
      if (props.mode === 'date-time') {
        datePickerErrorMessage.value = t('Shared.Globals.Min start dateTime', {
          minStartDate: minDateTime.setLocale(userStore.currentRegionalSettings.lang).toLocaleString(DateTime.DATETIME_SHORT),
        })
      } else {
        datePickerErrorMessage.value = t('Shared.Globals.Min start date', {
          minStartDate: minDateTime.setLocale(userStore.currentRegionalSettings.lang).toLocaleString(DateTime.DATE_SHORT),
        })
      }
      return datePickerErrorMessage.value
    }
  }

  if (props.max) {
    const maxDateTime = DateTime.fromISO(props.max)
    if (dt > maxDateTime) {
      if (props.mode === 'date-time') {
        datePickerErrorMessage.value = t('Shared.Globals.Max end dateTime', {
          maxEndDate: maxDateTime.setLocale(userStore.currentRegionalSettings.lang).toLocaleString(DateTime.DATETIME_SHORT),
        })
      } else {
        datePickerErrorMessage.value = t('Shared.Globals.Max end date', {
          maxEndDate: maxDateTime.setLocale(userStore.currentRegionalSettings.lang).toLocaleString(DateTime.DATE_SHORT),
        })
      }
      return datePickerErrorMessage.value
    }
  }

  return true
}

const setToday = () => {
  if (props.mode === 'time') {
    return
  }

  const now = DateTime.now()

  if (props.mode === 'date') {
    const today = now.toISODate()

    if (!validateOptions(today)) {
      return
    }

    emit('update:modelValue', today)
  } else if (props.mode === 'date-time') {
    let base = null

    if (props.modelValue) {
      const parsed = props.respectTimeZoneOffset ? DateTime.fromISO(String(props.modelValue), { setZone: true }) : DateTime.fromISO(String(props.modelValue))

      if (parsed.isValid) {
        base = parsed
      }
    }

    if (!base) {
      base = now
    }

    const combined = now.set({
      hour: base.hour,
      minute: base.minute,
      second: 0,
      millisecond: 0,
    })

    emit('update:modelValue', combined.toISO())
  }

  if (props.closeDateOnSelect) {
    if (datePopup.value) {
      datePopup.value.hide()
    }
    if (navPopup.value) {
      navPopup.value.hide()
    }
  }
}

const setNow = () => {
  const now = DateTime.now()

  if (props.mode === 'time') {
    const timeFormat = userStore.currentRegionalSettings.timeFormat || 'HH:mm'
    emit('update:modelValue', now.toFormat(timeFormat))

    if (props.closeTimeOnSelect && timePopup.value) {
      timePopup.value.hide()
    }
    return
  }

  if (props.mode === 'date-time') {
    let baseDate = null

    if (selectedDate.value) {
      const parsedDate = DateTime.fromISO(selectedDate.value)
      if (parsedDate.isValid) {
        baseDate = parsedDate
      }
    }

    if (!baseDate && props.modelValue) {
      const existing = props.respectTimeZoneOffset ? DateTime.fromISO(String(props.modelValue), { setZone: true }) : DateTime.fromISO(String(props.modelValue))

      if (existing.isValid) {
        baseDate = existing
      }
    }

    if (!baseDate) {
      baseDate = now
    }

    const combined = baseDate.set({
      hour: now.hour,
      minute: now.minute,
      second: 0,
      millisecond: 0,
    })

    emit('update:modelValue', combined.toISO())
  }

  if (props.closeTimeOnSelect && timePopup.value) {
    timePopup.value.hide()
  }
}

const handleCustomSetNow = () => {
  const now = DateTime.local()
  customHours.value = now.hour.toString().padStart(2, '0')
  customMinutes.value = now.minute.toString().padStart(2, '0')

  setNow()
}

const isValidKey = (key) => {
  const controlKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab']
  return !Number.isNaN(Number(key)) || controlKeys.includes(key)
}

const isValidValue = (type, value) => {
  const numberValue = parseInt(value, 10)
  if (Number.isNaN(numberValue)) {
    return false
  }
  if (type === 'hours') {
    return numberValue >= 0 && numberValue <= 23
  } else if (type === 'minutes') {
    return numberValue >= 0 && numberValue <= 59
  }
  return false
}

const focusNextTime = () => {
  if (customMinutesInputRef.value && typeof customMinutesInputRef.value.focus === 'function') {
    customMinutesInputRef.value.focus()
  }
}

const focusPreviousTime = () => {
  if (customHoursInputRef.value && typeof customHoursInputRef.value.focus === 'function') {
    customHoursInputRef.value.focus()
  }
}

const handleTimeInput = (event, type) => {
  event.preventDefault()
  const key = event.key

  if (!isValidKey(key)) {
    return
  }

  const currentRef = type === 'hours' ? customHours : customMinutes

  const selectionStart = event.target?.selectionStart || 0
  const selectionEnd = event.target?.selectionEnd || 0
  const selectionDiff = selectionEnd - selectionStart

  if (key === 'Backspace') {
    currentRef.value = currentRef.value.slice(0, event.ctrlKey || selectionDiff === 2 ? -2 : -1)
    if (type === 'minutes' && currentRef.value.length === 0) {
      focusPreviousTime()
    }
    return
  }

  if (key === 'Tab') {
    if (type === 'hours') {
      focusNextTime()
    } else {
      focusPreviousTime()
    }
    return
  }

  let currentValue = String(currentRef.value || '')

  if (currentValue.length === 2 && selectionDiff === 0) {
    currentValue = ''
  }

  let potentialValue = currentValue
  if (selectionDiff === 2) {
    potentialValue = key
  } else if (selectionDiff === 1) {
    const chars = currentValue.split('')
    chars[selectionStart] = key
    potentialValue = chars.join('')
  } else {
    potentialValue = currentValue + key
  }

  if (currentValue.length === 2 && key !== 'ArrowLeft' && key !== 'ArrowRight') {
    if (selectionDiff === 0) {
      if (type === 'hours') {
        focusNextTime()
      } else {
        focusPreviousTime()
      }
      return
    }
  } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
    return
  }

  if (!isValidValue(type, potentialValue)) {
    return
  }

  currentRef.value = potentialValue

  if (potentialValue.length === 2) {
    if (type === 'hours') {
      focusNextTime()
    } else {
      focusPreviousTime()
    }
  } else if (potentialValue.length === 1) {
    if (type === 'hours' ? Number(key) > 2 : Number(key) > 5) {
      currentRef.value = currentRef.value.padStart(2, '0')
      if (type === 'hours') {
        focusNextTime()
      } else {
        focusPreviousTime()
      }
    }
  }
}

const incrementTimeValue = (type) => {
  const currentRef = type === 'hours' ? customHours : customMinutes
  let currentValue = parseInt(currentRef.value, 10)
  if (Number.isNaN(currentValue)) {
    currentValue = 0
  }

  if (type === 'hours') {
    currentValue = (currentValue + 1) % 24
  } else if (type === 'minutes') {
    currentValue = (currentValue + 1) % 60
  }

  currentRef.value = currentValue.toString().padStart(2, '0')
}

const decrementTimeValue = (type) => {
  const currentRef = type === 'hours' ? customHours : customMinutes
  let currentValue = parseInt(currentRef.value, 10)
  if (Number.isNaN(currentValue)) {
    currentValue = 0
  }

  if (type === 'hours') {
    currentValue = (currentValue + 23) % 24
  } else if (type === 'minutes') {
    currentValue = (currentValue + 59) % 60
  }

  currentRef.value = currentValue.toString().padStart(2, '0')
}

const initCustomTimeFromSelected = () => {
  suppressCustomTimeWatch.value = true

  const src = selectedTime.value || ''
  if (!src || !src.includes(':')) {
    customHours.value = ''
    customMinutes.value = ''
    suppressCustomTimeWatch.value = false
    return
  }

  const [h, m] = src.split(':')
  customHours.value = h || ''
  customMinutes.value = m || ''

  suppressCustomTimeWatch.value = false
}

const onTimePopupBeforeShow = () => {
  if (selectedTime.value && typeof selectedTime.value === 'string' && selectedTime.value.includes(':')) {
    timeAtPopupOpen.value = selectedTime.value
  } else {
    timeAtPopupOpen.value = null
  }

  if (useCustomTimePickerLocal.value) {
    initCustomTimeFromSelected()
  }
}

const onMaskedInputKeydown = (event) => {
  const key = event.key

  if (!/^[0-9]$/.test(key)) {
    return
  }

  const inputEl = event.target
  const value = inputEl.value || ''
  const length = value.length

  if (value.includes('_')) {
    return
  }

  let start = inputEl.selectionStart ?? 0
  const end = inputEl.selectionEnd ?? start

  if (end > start + 1) {
    return
  }

  if (start >= length) {
    return
  }

  let index = start
  while (index < length && !/[0-9_]/.test(value[index])) {
    index += 1
  }

  if (index >= length) {
    return
  }

  inputEl.setSelectionRange(index, index + 1)
}

// Watchers
watchEffect(() => {
  if (!props.modelValue) {
    selectedDate.value = null
    selectedTime.value = null
    return
  }

  if (props.mode === 'time') {
    selectedDate.value = null
    selectedTime.value = props.modelValue
    return
  }

  const dt = props.respectTimeZoneOffset ? DateTime.fromISO(props.modelValue, { setZone: true }) : DateTime.fromISO(props.modelValue)

  if (dt.isValid) {
    selectedDate.value = dt.toISODate()
    if (props.mode === 'date-time') {
      selectedTime.value = dt.toFormat('HH:mm')
    } else {
      selectedTime.value = null
    }
  } else {
    selectedDate.value = null
    selectedTime.value = null
  }
})

watch(
  () => [customHours.value, customMinutes.value],
  ([h, m]) => {
    if (suppressCustomTimeWatch.value) {
      return
    }
    if (!useCustomTimePickerLocal.value) {
      return
    }
    if (props.mode === 'date') {
      return
    }
    if (!h || !m) {
      return
    }

    const value = `${h}:${m}`

    if (value === (selectedTime.value || '')) {
      return
    }

    if (!DateTime.fromISO(value).isValid) {
      return
    }

    selectedTime.value = value
    onTimeChanged(value)
  },
)

watch(
  () => props.useCustomTimePicker,
  (val) => {
    useCustomTimePickerLocal.value = val
    if (val) {
      initCustomTimeFromSelected()
    }
  },
)

// Define expose
defineExpose({ datePickerErrorMessage })
</script>

<template>
  <!-- Navigation mode: behaves as a button showing a formatted period label -->
  <q-btn v-if="navigationMode" v-bind="$attrs" :label="displayValue || ' '" :disable="$attrs.readonly">
    <q-popup-proxy v-if="!$attrs.readonly" ref="navPopup" transition-show="scale" transition-hide="scale">
      <div class="row no-wrap items-start sc-nav-qdate-wrap q-pa-sm">
        <!-- Week numbers column (navigation mode) -->
        <div v-if="showWeekNumbers" class="column items-center q-ml-sm q-mr-xs sc-nav-weeknums">
          <div v-for="w in navWeekNumbers" :key="w.key" class="sc-nav-weeknums-item font-size-rem-9 text-grey-5">
            {{ w.number }}
          </div>
        </div>

        <!-- QDate in navigation mode (day/week/month select) -->
        <q-date
          v-model="navSelectedDates"
          :locale="{
            days: t('Shared.Globals.Datepicker.days').split('_'),
            daysShort: t('Shared.Globals.Datepicker.daysShort').split('_'),
            months: t('Shared.Globals.Datepicker.months').split('_'),
            monthsShort: t('Shared.Globals.Datepicker.monthsShort').split('_'),
            firstDayOfWeek: 1,
            pluralDay: t('Shared.Globals.Datepicker.pluralDay'),
          }"
          multiple
          no-unset
          minimal
          flat
          event-color="primary"
          hide-bottom-space
          :clearable="clearable"
          class="sc-nav-qdate q-field--error"
          mask="YYYY-MM-DD"
          :options="validateOptions"
          @navigation="onNavNavigation"
        >
          <!-- Today shortcut (navigation mode) -->
          <q-btn
            v-if="!noTodayBtn"
            flat
            dense
            icon="today"
            :label="t('Shared.Globals.Today')"
            color="primary"
            class="q-mt-lg q-px-sm q-py-xs"
            @click.stop="setToday"
          />
        </q-date>
      </div>
    </q-popup-proxy>
  </q-btn>

  <!-- Default mode: QInput with date and optional time popup -->
  <q-input
    v-else
    :model-value="displayValue"
    v-bind="$attrs"
    ref="dateInputField"
    outlined
    :stack-label="!!displayValue"
    :hide-bottom-space="hideBottomSpace"
    fill-mask="_"
    :mask="currentMask"
    :rules="[validateDate]"
    :clearable="clearable && !!modelValue"
    @update:model-value="onManualInput"
    @keydown="onMaskedInputKeydown"
  >
    <template #append>
      <!-- Date icon + QDate popup -->
      <q-icon v-if="mode !== 'time'" name="event" class="cursor-pointer" :class="{ 'no-pointer-events': $attrs.readonly }">
        <q-popup-proxy v-if="!$attrs.readonly" ref="datePopup" transition-show="scale" transition-hide="scale">
          <div class="row no-wrap items-start sc-nav-qdate-wrap q-pa-sm">
            <!-- Week numbers column (normal mode) -->
            <div v-if="showWeekNumbers" class="column items-center q-ml-sm q-mr-xs sc-nav-weeknums">
              <div v-for="w in navWeekNumbers" :key="w.key" class="sc-nav-weeknums-item font-size-rem-9 text-grey-5">
                {{ w.number }}
              </div>
            </div>

            <!-- QDate for concrete day selection -->
            <q-date
              v-model="selectedDateProxy"
              :locale="{
                days: t('Shared.Globals.Datepicker.days').split('_'),
                daysShort: t('Shared.Globals.Datepicker.daysShort').split('_'),
                months: t('Shared.Globals.Datepicker.months').split('_'),
                monthsShort: t('Shared.Globals.Datepicker.monthsShort').split('_'),
                firstDayOfWeek: 1,
                pluralDay: t('Shared.Globals.Datepicker.pluralDay'),
              }"
              no-unset
              minimal
              flat
              event-color="primary"
              hide-bottom-space
              :clearable="clearable"
              class="sc-nav-qdate q-field--error"
              mask="YYYY-MM-DD"
              :options="validateOptions"
              @navigation="onNavNavigation"
            >
              <!-- Today shortcut (normal mode) -->
              <q-btn
                v-if="!noTodayBtn"
                flat
                dense
                icon="today"
                :label="t('Shared.Globals.Today')"
                color="primary"
                class="q-mt-lg q-px-sm q-py-xs"
                @click.stop="setToday"
              />
            </q-date>
          </div>
        </q-popup-proxy>
      </q-icon>

      <!-- Time icon + time picker popup -->
      <q-icon v-if="mode !== 'date'" name="access_time" class="cursor-pointer q-ml-sm" :class="{ 'no-pointer-events': $attrs.readonly }">
        <q-popup-proxy
          v-if="!$attrs.readonly"
          ref="timePopup"
          transition-show="scale"
          transition-hide="scale"
          class="flex justify-center min-width-px-300"
          @before-show="onTimePopupBeforeShow"
        >
          <!-- Custom stepper-style time picker -->
          <div v-if="useCustomTimePickerLocal" class="q-pa-md">
            <div class="flex no-wrap items-center justify-center p-2">
              <!-- Hours column -->
              <div class="column items-center no-wrap">
                <div class="flex">
                  <q-btn color="black" unelevated flat icon="arrow_drop_up" dense @click="incrementTimeValue('hours')" />
                </div>
                <div class="flex">
                  <q-input
                    ref="customHoursInputRef"
                    type="number"
                    :model-value="customHours"
                    mask="##"
                    :label="!customHours ? 'HH' : undefined"
                    outlined
                    input-class="text-center"
                    input-style="font-size: 1.25rem; width: 80px;"
                    class="q-field--error no-number-arrows"
                    autofocus
                    @keydown="(evt) => handleTimeInput(evt, 'hours')"
                  />
                </div>
                <div class="flex">
                  <q-btn color="black" unelevated flat icon="arrow_drop_down" dense @click="decrementTimeValue('hours')" />
                </div>
              </div>

              <!-- Separator -->
              <span class="font-bold text-h5 q-my-auto q-px-md">:</span>

              <!-- Minutes column -->
              <div class="column items-center">
                <div class="flex">
                  <q-btn color="black" unelevated flat icon="arrow_drop_up" dense @click="incrementTimeValue('minutes')" />
                </div>
                <div class="flex">
                  <q-input
                    ref="customMinutesInputRef"
                    type="number"
                    :model-value="customMinutes"
                    mask="##"
                    :label="!customMinutes ? 'mm' : undefined"
                    outlined
                    input-class="text-center"
                    input-style="font-size: 1.25rem; width: 80px;"
                    class="q-field--error no-number-arrows"
                    @keydown="(evt) => handleTimeInput(evt, 'minutes')"
                  />
                </div>
                <div class="flex">
                  <q-btn color="black" unelevated flat icon="arrow_drop_down" dense @click="decrementTimeValue('minutes')" />
                </div>
              </div>
            </div>

            <!-- Custom picker actions -->
            <div class="flex justify-between q-mt-md">
              <q-btn
                v-if="!noNowBtn"
                icon="sym_o_update"
                color="primary"
                class="q-px-sm q-py-xs"
                flat
                dense
                :label="t('Shared.Globals.Current time')"
                @click="handleCustomSetNow"
              />
              <q-btn v-if="showClockSwitcher" flat dense icon="sym_o_schedule" color="primary" class="q-ml-xs" @click="toggleTimePickerMode" />
            </div>
          </div>

          <!-- Native QTime variant -->
          <template v-else>
            <q-time
              v-model="selectedTime"
              :format24h="userStore.currentRegionalSettings.lang !== 'en-US'"
              @update:model-value="onTimeChanged"
              outlined
              flat
              class="q-field--error full-width"
            >
              <div class="row justify-between q-px-sm q-py-xs">
                <q-btn v-if="!noNowBtn" flat dense icon="sym_o_update" :label="t('Shared.Globals.Current time')" color="primary" @click="setNow" />
                <q-btn v-if="showClockSwitcher" flat dense icon="sym_o_keyboard" color="primary" @click="toggleTimePickerMode" />
              </div>
            </q-time>
          </template>
        </q-popup-proxy>
      </q-icon>

      <slot name="append" />
    </template>
  </q-input>
</template>

<style scoped>
/* Week number column alignment for navigation mode */
.sc-nav-weeknums {
  padding-top: 65px;
}

.sc-nav-weeknums-item {
  height: 32px;
  display: flex;
  align-items: center;
  margin-top: 4px;
}

/* Hide week numbers when month/year pickers are shown */
.sc-nav-qdate-wrap:has(.q-date__months) .sc-nav-weeknums-item,
.sc-nav-qdate-wrap:has(.q-date__years) .sc-nav-weeknums-item {
  visibility: hidden;
}

/* Slightly tighter vertical spacing between calendar rows (fixes "1 misaligned" issue) */
.sc-nav-qdate :deep(.q-date__calendar-days > div + div) {
  margin-top: 2px;
  margin-bottom: 2px;
}

/* Smooth hover look by normalizing the day buttons */
.sc-nav-qdate :deep(.q-date__calendar-item .q-btn) {
  border-radius: 16px;
  width: 40px;
  box-shadow: none;
}

/* Selected range (week/month) – use the day cell as the highlight bar */
.sc-nav-qdate :deep(.q-date__calendar-item .q-btn.bg-primary) {
  background-color: transparent !important;
  color: #000 !important;
  box-shadow: none !important;
}

.sc-nav-qdate :deep(.q-date__calendar-item:has(.q-btn.bg-primary)) {
  background-color: color-mix(in srgb, var(--q-primary) 18%, transparent);
  border-radius: 0;
}

/* Left edge of the selected range bar */
.sc-nav-qdate :deep(.q-date__calendar-item:first-child:has(.q-btn.bg-primary)),
.sc-nav-qdate :deep(.q-date__calendar-item:not(:has(.q-btn.bg-primary)) + .q-date__calendar-item:has(.q-btn.bg-primary)) {
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;
}

/* Right edge of the selected range bar */
.sc-nav-qdate :deep(.q-date__calendar-item:has(.q-btn.bg-primary):not(:has(+ .q-date__calendar-item .q-btn.bg-primary))) {
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
}

/* Highlight "today" with a subtle ring, regardless of selection state */
.sc-nav-qdate :deep(.q-date__calendar-item .q-btn.q-date__today) {
  border-radius: 999px !important;
  box-shadow: 0 0 0 1px var(--q-primary) inset !important;
}
</style>
